import React from "react";

import {
  Grid,
  TextControl,
  Form,
  Divider,
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  DateControl,
  AutocompleteControl,
  NumberControl,
} from "@tiny/components";
import { CustomerInfoView } from "@tiny/api";
import { useEditSubscriberDialog } from "./EditSubscriberDialog.hooks";
import { validateEmail } from "@tiny/components/src/utils/validateUtils";

export interface EditSubscriberDialogProps {
  open: boolean;
  customer?: CustomerInfoView;
  onClose: () => void;
  onSuccess: () => void;
}

function EditSubscriberDialog(props: EditSubscriberDialogProps) {
  const { open, onClose } = props;

  const { form, submitForm, save, saving, customerTags } =
    useEditSubscriberDialog(props);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      disableBackdropClose
      disableScrollLock
    >
      <DialogTitle
        onClose={onClose}
        subtitle="Edit information about this subscriber. More data about individual subscribers coming soon."
      >
        Edit
      </DialogTitle>
      <DialogContent>
        <Form form={form} onSubmit={submitForm}>
          <Grid container spacing={6} verticalSpacing={1}>
            <Grid item xs={12} md={12}>
              <TextControl
                name="email"
                defaultValue=""
                label="Email"
                showHelperTextMargin
                required
                validate={validateEmail}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="firstName"
                defaultValue=""
                label="First Name"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="lastName"
                defaultValue=""
                label="Last Name"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="source"
                defaultValue=""
                label="Source"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateControl
                name="birthday"
                defaultValue=""
                label="Birthday"
                showHelperTextMargin
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <AutocompleteControl
                name="tags"
                options={customerTags}
                label="Tags"
                showHelperTextMargin
                multiple
                freeSolo
                getOptionLabel={(option: string) => option}
                autoSelect
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="currency"
                defaultValue=""
                label="Currency"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="country"
                defaultValue=""
                label="Country"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="province"
                defaultValue=""
                label="State/Province"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="city"
                defaultValue=""
                label="City"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextControl
                name="address1"
                defaultValue=""
                label="Address 1"
                showHelperTextMargin
                maxLength={128}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextControl
                name="address2"
                defaultValue=""
                label="Address 2"
                showHelperTextMargin
                maxLength={128}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="phone"
                defaultValue=""
                label="Phone number"
                showHelperTextMargin
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="postalCode"
                defaultValue=""
                label="Zip/Postal Code"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="company"
                defaultValue=""
                label="Company"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberControl
                name="ordersCount"
                defaultValue=""
                label="Number of orders"
                showHelperTextMargin
                max={2147483647}
                min={0}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberControl
                name="totalSpent"
                defaultValue=""
                label="Lifetime value"
                showHelperTextMargin
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="lastOrderId"
                defaultValue=""
                label="Latest order ID"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextControl
                name="lastOrderName"
                defaultValue=""
                label="Last purchased order"
                showHelperTextMargin
                maxLength={64}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateControl
                name="lastOrderDate"
                defaultValue=""
                label="Last purchased date"
                showHelperTextMargin
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberControl
                name="lastOrderPrice"
                defaultValue=""
                label="Last purchased price"
                showHelperTextMargin
              />
            </Grid>
          </Grid>
          <Divider mt={6} />
          <button type="submit" hidden />
        </Form>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          loading={saving}
          disabled={saving}
          onClick={save}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditSubscriberDialog;
